import { Close, DragIndicator, OpenInNew } from '@mui/icons-material';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface OverlaySystemBarProps {
    navigateUrl?: string;
    onClose: () => void;
}

/**
 * OverlaySystemBar - System bar for overlays with drag, close and maximize functionality
 */
function OverlaySystemBar({ navigateUrl, onClose }: OverlaySystemBarProps) {
    const theme = useTheme();
    const navigate = useNavigate();

    const handleMaximize = () => {
        if (navigateUrl) {
            navigate(navigateUrl);
            onClose(); // Close overlay after navigation
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: `linear-gradient(135deg, 
                    ${theme.palette.primary.main} 0%, 
                    ${theme.palette.primary.dark} 50%, 
                    ${theme.palette.secondary.main} 100%
                )`,
                color: theme.palette.primary.contrastText,
                px: 1.5,
                py: 0.75,
                userSelect: 'none',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, 
                        rgba(255, 255, 255, 0.1) 0%, 
                        rgba(255, 255, 255, 0.05) 50%, 
                        rgba(255, 255, 255, 0) 100%
                    )`,
                    pointerEvents: 'none',
                },
            }}
        >
            {/* Left: Drag Handle */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DragIndicator sx={{ fontSize: 20, opacity: 0.7 }} />
            </Box>

            {/* Right: Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {navigateUrl && (
                    <IconButton
                        size="small"
                        onClick={handleMaximize}
                        sx={{
                            color: 'inherit',
                            zIndex: 1,
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                        title="Zur Seite navigieren"
                    >
                        <OpenInNew sx={{ fontSize: 20 }} />
                    </IconButton>
                )}
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        color: 'inherit',
                        zIndex: 1,
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                    }}
                    title="Schließen"
                >
                    <Close sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
        </Box>
    );
}

export default OverlaySystemBar;
